import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useGetUserProfileQuery, useUpdateUserProfileMutation } from '@/store/api/userApi';
import { setChosenGender, setGenderFilter, type ShopGender } from '@/store/slices/preferencesSlice';

/** "male" / "Female" / anything else, as the filter the shop lists use. */
export function toShopGender(gender: string | null | undefined): ShopGender | null {
  const g = (gender ?? '').trim().toLowerCase();
  return g === 'male' ? 'MALE' : g === 'female' ? 'FEMALE' : null;
}

/**
 * Keeps the shop lists' gender filter in step with who is browsing: a signed-in customer's profile
 * gender, or the choice made on the onboarding screen before there was an account. The "only show
 * shops that serve me" switch turns it off. The API layer adds the filter to every discovery request
 * from the stored value, and changing it empties the cache so the lists reload.
 *
 * A gender chosen on onboarding is saved to the account the first time one signs in without a gender
 * of its own; an account that already has one keeps it.
 */
export function useSyncGenderFilter() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const showOnlyMine = useAppSelector((state) => state.preferences.showOnlyMyGender);
  const chosen = useAppSelector((state) => state.preferences.chosenGender);
  const current = useAppSelector((state) => state.preferences.genderFilter);
  const profile = useGetUserProfileQuery(undefined, { skip: !user });
  const [updateProfile] = useUpdateUserProfileMutation();
  const saving = useRef(false);

  useEffect(() => {
    if (!user) {
      const next = showOnlyMine ? toShopGender(chosen) : null;
      if (next !== current) dispatch(setGenderFilter(next));
      return;
    }
    // Wait for the profile: guessing before it loads would flip the lists twice.
    if (!profile.data) return;
    const accountGender = (profile.data.gender ?? '').trim();
    const next = showOnlyMine ? toShopGender(accountGender || chosen) : null;
    if (next !== current) dispatch(setGenderFilter(next));

    if (!accountGender && chosen && !saving.current) {
      saving.current = true;
      void updateProfile({ gender: chosen })
        .unwrap()
        .then(() => dispatch(setChosenGender(null)))
        .catch(() => undefined)
        .finally(() => {
          saving.current = false;
        });
    }
  }, [user, profile.data, showOnlyMine, chosen, current, dispatch, updateProfile]);
}
